import React, { useEffect, useRef, useState } from 'react';
import { Input, LoaderIcon, useClickOutside } from '@devesharp/react/web';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form } from '@unform/web';
import { Link } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import Style from './SellerSelect.style';
import { useViewList } from '~/hooks/useViewList';
import { CompaniesGetResolve, CompaniesSearchResolve } from '~/api';
import { CNPJAndCPFMask } from '~/services';
import { useDebounce } from '~/hooks/useDebounce';
import {
   companyPageSelectState,
   companySelectState,
   companyShowInfoState,
   normalizeCompany,
   uniqueCompanyUserState,
} from '~/recoil/company-select.state';
import type { Company } from '~/recoil/company-select.state';
import { useUser } from '~/hooks';

function mergeCompany(
   current: Company | null,
   data: Partial<Company> & { business_name?: string },
): Company {
   return normalizeCompany({
      ...(current ?? {}),
      ...data,
      id: data.id ?? current?.id ?? 0,
      name: data.name ?? data.business_name ?? current?.name ?? '',
   } as Company);
}

export function SellerSelect(): any {
   const user = useUser();
   const userCompanies = user.companies ?? user.sellers ?? [];
   const [value, setValue] = useState('');
   const dropRef = useRef(null);
   const [sellerPageSelect, setSellerPageSelect] = useRecoilState(companyPageSelectState);
   const [sellerSelect, setSellerSelect] = useRecoilState(companySelectState);
   const [, setShowInfoSeller] = useRecoilState(companyShowInfoState);
   const [selectedName, setSelectedName] = useState(sellerPageSelect?.name ?? 'Todos os estabelecimentos');
   const [uniqueSeller, setUniqueSeller] = useRecoilState(uniqueCompanyUserState);

   useEffect(() => {
      if (userCompanies.length == 1 && user.role != 'MASTER') {
         const company = normalizeCompany(userCompanies[0]);
         setUniqueSeller(true);
         setShowInfoSeller(false);
         setSelectedName(company.name);
         setSellerSelect(company);
         setSellerPageSelect(company);
         updateBalance(company);
      } else {
         setUniqueSeller(false);
      }
   }, []);

   useEffect(() => {
      if (!sellerSelect && (userCompanies.length > 1 || user.role == 'MASTER')) {
         setShowInfoSeller(true);
      }
   }, [sellerSelect]);

   useEffect(() => {
      if (sellerPageSelect?.id) {
         updateBalance(sellerPageSelect);
      }
   }, []);

   function updateBalance(company: Company): void {
      CompaniesGetResolve(company.id).subscribe((data) => {
         const merged = mergeCompany(company, data);
         setSellerPageSelect(merged);
         setSellerSelect(merged);
      });
   }

   const [visibleDropDown, setVisibleDropDown] = useState(false);

   const { resources, setFilters, searching, starting } = useViewList({
      resolves: {
         items: CompaniesSearchResolve,
      },
   });

   useClickOutside(() => {
      setVisibleDropDown(false);
   }, dropRef);

   function onSelectSeller(seller: any): void {
      if (!seller) {
         setSellerPageSelect(null);
         setSellerSelect(null);
         setSelectedName('Todos os estabelecimentos');
      } else {
         setSelectedName(seller.name);
         const company = normalizeCompany(seller);
         setSellerPageSelect(company);
         setSellerSelect(company);
         updateBalance(company);
      }
      setVisibleDropDown(false);
      setValue('');
   }

   const valueSearch = useDebounce(value, 250);

   useEffect(() => {
      setFilters(() => ({
         name: valueSearch,
      }));
   }, [valueSearch]);

   if (uniqueSeller) {
      return null;
   }

   return (
      <Style ref={dropRef}>
         <div>
            <div className="d-flex align-items-end" onClick={() => setVisibleDropDown((i) => !i)}>
               <div>
                  <div className="text-title">Você está como</div>
                  <div className="selected">{selectedName}</div>
               </div>
               <div className="arrow">
                  <svg
                     height="20"
                     width="20"
                     viewBox="0 0 20 20"
                     aria-hidden="true"
                     focusable="false"
                     fill="white"
                     className="css-6q0nyr-Svg"
                  >
                     <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
                  </svg>
               </div>
            </div>

            {visibleDropDown && (
               <div className="dropdown">
                  <div className="search">
                     <Form onSubmit={() => {}}>
                        <Input
                           name="sdsd"
                           placeholder="Buscar CPF, CNPJ ou Nome"
                           onKeyUp={(e: any) => {
                              setValue(e.target.value);
                           }}
                           suffix={
                              <>
                                 <FontAwesomeIcon icon={faSearch} color="black" />
                              </>
                           }
                        />
                     </Form>
                  </div>

                  <div className="listing">
                     {(searching || starting) && (
                        <div className="loading-page">
                           <LoaderIcon />
                        </div>
                     )}
                     {!starting && (
                        <div className="item" onClick={() => onSelectSeller(null)}>
                           <div className="name">Todos</div>
                           <div className="document">Visualizar todos estabelecimentos</div>
                        </div>
                     )}
                     {!searching &&
                        resources?.map((resource: any) => (
                           <div className="item" onClick={() => onSelectSeller(resource)}>
                              <div className="name">{resource.name}</div>
                              <div className="document">
                                 {resource.document ? (
                                    <>
                                       {resource.document.length > 11 ? 'CNPJ' : 'CPF'}{' '}
                                       {CNPJAndCPFMask(resource.document)}
                                    </>
                                 ) : (
                                    'Documento não informado'
                                 )}
                              </div>
                           </div>
                        ))}
                     {!searching && resources.length === 0 && !searching && !starting && (
                        <div className="empty">Nenhum estabelecimento encontrado</div>
                     )}
                  </div>
                  <div className="see-all">
                     <Link to="/establishment">Ver todos</Link>
                  </div>
               </div>
            )}
         </div>
      </Style>
   );
}
